import React, { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMarketplaceDeals } from '@/hooks/useMarketplaceDeals';
import { MarketplaceDealCard } from '@/components/marketplace/MarketplaceDealCard';
import { MarketplaceFilters } from '@/components/marketplace/MarketplaceFilters';
import { MarketplaceDealDetailModal } from '@/components/marketplace/MarketplaceDealDetailModal';
import { MarketplaceDeal } from '@/hooks/useMarketplaceDeals';
import { useUser } from '@/contexts/UserContext';
import { EmptyState } from '@/components/EmptyState';
import { ShoppingBag, TrendingUp, Clock, CheckCircle, Search, FileText } from 'lucide-react';
import { PageOnboardingTooltip, OnboardingSlide } from '@/components/onboarding/PageOnboardingTooltip';

const marketplaceSlides: OnboardingSlide[] = [
  { icon: ShoppingBag, secondaryIcons: [Search], title: 'Welcome to the Marketplace', description: 'Browse available deals from brands and ventures. Filter by category, value, and type to find opportunities that match your profile.', gradient: 'from-primary/20 to-primary/5' },
  { icon: FileText, secondaryIcons: [Clock], title: 'Apply & Claim Deals', description: 'Found a deal you like? Claim it to express interest. Track your pending applications and see which deals you\'ve already secured.', gradient: 'from-blue-500/20 to-blue-500/5' },
  { icon: CheckCircle, secondaryIcons: [TrendingUp], title: 'Track Your Progress', description: 'Switch between Available, Pending, and Claimed tabs to stay on top of every opportunity at each stage.', gradient: 'from-emerald-500/20 to-emerald-500/5' },
];

export const Marketplace: React.FC = () => {
  const {
    deals,
    loading,
    error,
    claimDeal,
    getMyClaimedDeals,
    getAvailableDeals,
    getActiveDeals,
    getPendingDeals,
    fetchMarketplaceDeals
  } = useMarketplaceDeals();

  const { accountType } = useUser();
  const [selectedDeal, setSelectedDeal] = useState<MarketplaceDeal | null>(null);
  const [activeTab, setActiveTab] = useState('available');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [dealTypeFilter, setDealTypeFilter] = useState('all');
  const [valueRangeFilter, setValueRangeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const filteredDeals = useMemo(() => {
    let filtered = [...deals];
    
    switch (activeTab) {
      case 'available':
        filtered = getAvailableDeals();
        break;
      case 'active':
        filtered = getActiveDeals();
        break;
      case 'claimed':
        filtered = getMyClaimedDeals();
        break;
      case 'pending-review':
        filtered = getPendingDeals();
        break;
      default:
        filtered = deals;
    }
    
    if (searchTerm) {
      filtered = filtered.filter(deal =>
        deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.brand_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (dealTypeFilter !== 'all') {
      filtered = filtered.filter(deal => deal.deal_type === dealTypeFilter);
    }
    
    if (valueRangeFilter !== 'all') {
      const [min, max] = valueRangeFilter.split('-').map(v => v.replace('+', ''));
      filtered = filtered.filter(deal => {
        if (valueRangeFilter === '20000+') {
          return deal.deal_value >= 20000;
        }
        return deal.deal_value >= parseInt(min) && deal.deal_value <= parseInt(max);
      });
    }
    
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'value-high':
        filtered.sort((a, b) => b.deal_value - a.deal_value);
        break;
      case 'value-low':
        filtered.sort((a, b) => a.deal_value - b.deal_value);
        break;
      case 'deadline':
        filtered.sort((a, b) => {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        });
        break;
    }
    
    return filtered;
  }, [deals, activeTab, searchTerm, dealTypeFilter, valueRangeFilter, sortBy, getAvailableDeals, getActiveDeals, getMyClaimedDeals, getPendingDeals]);

  const clearFilters = () => {
    setSearchTerm('');
    setDealTypeFilter('all');
    setValueRangeFilter('all');
    setSortBy('newest');
  };

  const hasActiveFilters = Boolean(searchTerm) || dealTypeFilter !== 'all' || valueRangeFilter !== 'all' || sortBy !== 'newest';

  const userCanClaim = accountType === 'talent';

  const availableCount = getAvailableDeals().length;
  const activeCount = getActiveDeals().length;
  const claimedCount = getMyClaimedDeals().length;
  const pendingReviewCount = getPendingDeals().length;

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading marketplace deals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <EmptyState
          title="Error Loading Deals"
          description={error}
          icon={<ShoppingBag className="w-8 h-8 text-muted-foreground" />}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <PageOnboardingTooltip storageKey="marketplace_tooltip_dismissed" slides={marketplaceSlides} dialogTitle="Marketplace Walkthrough" />
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Deals</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <TrendingUp className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{availableCount}</div>
            <p className="text-xs text-muted-foreground">Ready to claim</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <CheckCircle className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCount}</div>
            <p className="text-xs text-muted-foreground">Currently in progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Claims</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <FileText className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{claimedCount}</div>
            <p className="text-xs text-muted-foreground">Applications submitted</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="available" className="relative">
            Available Deals
            {availableCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center">
                {availableCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="active" className="relative">
            Active Deals
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center">
                {activeCount}
              </Badge>
            )}
          </TabsTrigger>
          {userCanClaim && (
            <TabsTrigger value="claimed" className="relative">
              My Claims
              {claimedCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center">
                  {claimedCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {userCanClaim && (
            <TabsTrigger value="pending-review" className="relative">
              Pending Review
              {pendingReviewCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center">
                  {pendingReviewCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Filters */}
        <MarketplaceFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dealTypeFilter={dealTypeFilter}
          onDealTypeChange={setDealTypeFilter}
          valueRangeFilter={valueRangeFilter}
          onValueRangeChange={setValueRangeFilter}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />

        <TabsContent value="available" className="space-y-4">
          {filteredDeals.length === 0 ? (
            <EmptyState
              title="No Available Deals"
              description="There are no deals matching your current filters. Try adjusting your search criteria."
              icon={<ShoppingBag className="w-8 h-8 text-muted-foreground" />}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDeals.map((deal) => (
                <MarketplaceDealCard
                  key={deal.id}
                  deal={deal}
                  onViewDetails={setSelectedDeal}
                  onClaimDeal={claimDeal}
                  userCanClaim={userCanClaim}
                  onApplicationRevoked={fetchMarketplaceDeals}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          {filteredDeals.length === 0 ? (
            <EmptyState
              title="No Active Deals"
              description="There are no active deals at the moment. Deals become active once they've been accepted."
              icon={<CheckCircle className="w-8 h-8 text-muted-foreground" />}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDeals.map((deal) => (
                <MarketplaceDealCard
                  key={deal.id}
                  deal={deal}
                  onViewDetails={setSelectedDeal}
                  onClaimDeal={claimDeal}
                  userCanClaim={false}
                  onApplicationRevoked={fetchMarketplaceDeals}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {userCanClaim && (
          <TabsContent value="claimed" className="space-y-4">
            {filteredDeals.length === 0 ? (
              <EmptyState
                title="No Claimed Deals"
                description="You haven't claimed any deals yet. Browse available deals to get started."
                icon={<CheckCircle className="w-8 h-8 text-muted-foreground" />}
              />
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Showing your applications and active deals. Applications are pending brand review.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDeals.map((deal) => (
                    <MarketplaceDealCard
                      key={deal.id}
                      deal={deal}
                      onViewDetails={setSelectedDeal}
                      onClaimDeal={claimDeal}
                      userCanClaim={false}
                      onApplicationRevoked={fetchMarketplaceDeals}
                    />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        )}

        {userCanClaim && (
          <TabsContent value="pending-review" className="space-y-4">
            {filteredDeals.length === 0 ? (
              <EmptyState
                title="No Pending Applications"
                description="No brands have paired deals with you yet. Keep your profile updated to attract opportunities."
                icon={<Clock className="w-8 h-8 text-muted-foreground" />}
              />
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Brands have paired these deals with you. Review and accept or decline the opportunity.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDeals.map((deal) => (
                    <MarketplaceDealCard
                      key={deal.id}
                      deal={deal}
                      onViewDetails={setSelectedDeal}
                      onClaimDeal={claimDeal}
                      userCanClaim={false}
                      onApplicationRevoked={fetchMarketplaceDeals}
                    />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Deal Detail Modal */}
      <MarketplaceDealDetailModal
        deal={selectedDeal}
        isOpen={!!selectedDeal}
        onClose={() => setSelectedDeal(null)}
        onRefresh={fetchMarketplaceDeals}
      />
    </div>
  );
};
